import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { Header } from './components/common/Header';
import { Sidebar } from './components/common/Sidebar';
import { Dashboard } from './pages/Dashboard';
import { Users } from './pages/Users';
import { UserDetail } from './pages/UserDetail';
import { Groups } from './pages/Groups';
import { GroupDetail } from './pages/GroupDetail';
import { Licenses } from './pages/Licenses';
import { LicenseAssignment } from './pages/LicenseAssignment';
import { AzurePermissions } from './pages/AzurePermissions';
import { SharedMailboxes } from './pages/SharedMailboxes';
import { Contacts } from './pages/Contacts';
import { MailingLists } from './pages/MailingLists';
import { Onboarding } from './pages/Onboarding';
import { Offboarding } from './pages/Offboarding';
import { CategoryLicenses } from './pages/CategoryLicenses';
import { Settings } from './pages/Settings';
import { TeamViewerChannels } from './pages/TeamViewerChannels';
import { Login } from './pages/Login';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { apiClient } from './services/apiClient';
import { ROUTES } from './utils/constants';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0078d4',
    },
    secondary: {
      main: '#107c10',
    },
  },
});

const App: React.FC = () => {
  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    if (accounts.length > 0) {
      apiClient.setMsalInstance(instance);
    }
  }, [instance, accounts]);

  // Don't render until authentication state is determined
  if (inProgress === 'startup' || inProgress === 'handleRedirect') {
    return null;
  }

  const isAuthenticated = accounts.length > 0;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {isAuthenticated ? (
          <Box sx={{ display: 'flex' }}>
            <Header />
            <Sidebar />
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: 3,
                mt: 8,
                width: { sm: `calc(100% - 240px)` },
              }}
            >
              <Routes>
                <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                <Route
                  path={ROUTES.DASHBOARD}
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.USERS}
                  element={
                    <ProtectedRoute>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.USER_DETAIL}
                  element={
                    <ProtectedRoute>
                      <UserDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.GROUPS}
                  element={
                    <ProtectedRoute>
                      <Groups />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.GROUP_DETAIL}
                  element={
                    <ProtectedRoute>
                      <GroupDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.LICENSES}
                  element={
                    <ProtectedRoute>
                      <Licenses />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.LICENSE_ASSIGNMENT}
                  element={
                    <ProtectedRoute>
                      <LicenseAssignment />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.AZURE_PERMISSIONS}
                  element={
                    <ProtectedRoute>
                      <AzurePermissions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.SHARED_MAILBOXES}
                  element={
                    <ProtectedRoute>
                      <SharedMailboxes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.CONTACTS}
                  element={
                    <ProtectedRoute>
                      <Contacts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.MAILING_LISTS}
                  element={
                    <ProtectedRoute>
                      <MailingLists />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.ONBOARDING}
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.OFFBOARDING}
                  element={
                    <ProtectedRoute>
                      <Offboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.GITHUB_LICENSES}
                  element={
                    <ProtectedRoute>
                      <CategoryLicenses categoryName="GitHub" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.TEAMVIEWER_LICENSES}
                  element={
                    <ProtectedRoute>
                      <CategoryLicenses categoryName="TeamViewer" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.TEAMVIEWER_CHANNELS}
                  element={
                    <ProtectedRoute>
                      <TeamViewerChannels />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.AUTOCAD_LICENSES}
                  element={
                    <ProtectedRoute>
                      <CategoryLicenses categoryName="AutoCAD" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.TRIPLETEX_LICENSES}
                  element={
                    <ProtectedRoute>
                      <CategoryLicenses categoryName="Tripletex" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.TELARIS_LICENSES}
                  element={
                    <ProtectedRoute>
                      <CategoryLicenses categoryName="Telaris" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.ONSHAPE_LICENSES}
                  element={
                    <ProtectedRoute>
                      <CategoryLicenses categoryName="OnShape" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.SETTINGS}
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
              </Routes>
            </Box>
          </Box>
        ) : (
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
          </Routes>
        )}
      </Router>
    </ThemeProvider>
  );
};

export default App;
